import React, { useEffect, useRef, useState } from "react";
import { ASSETS_ROUTES } from "../lib/common/constants";
import { TApiTechnician, TLogoCustomization } from "../lib/types/dentalTechs";
import { FileUploaderComponent, useFileManagement } from "../services/file-management";
import { TProcessStates } from "../lib/types/general";
import { getCacheKillQueryString } from "../lib/common/api";
import { useTechnicians } from "../services/technicians";
import { useAuthContext } from "../contexts/auth-context";
import { useUserService } from "../services/users";

type TLogoTechProps = {
    userInfo: TApiTechnician;
    editable: boolean;
    workingState: TProcessStates;
    isSomePartBeingEdited: boolean;
    setInappropriateContent: (value: React.SetStateAction<boolean>) => void;
    setUnsupportedImageType: React.Dispatch<React.SetStateAction<boolean>>;
    setUploadErrorMessage?: React.Dispatch<React.SetStateAction<string>>;
    extraClass: string;
    onboardingStepCompleted?: () => void;
};
export const LogoTech = (props: TLogoTechProps) => {
    const { userInfo, editable, workingState, isSomePartBeingEdited, setInappropriateContent, setUnsupportedImageType, setUploadErrorMessage, extraClass } =
        props;
    const refLogoPhoto = useRef<HTMLDivElement>(null);
    const { makeDroppableCapable, getImagePath, makeDraggableCapableForImageResize } = useFileManagement();
    const [logoImg, setLogo] = useState<string | undefined>(undefined);
    const [logoCustomizationStyle, _setLogoCustomizationStyle] = useState<React.CSSProperties>({ backgroundSize: "cover" });
    const { updateLogoCustomization } = useTechnicians();
    const [isPhotoCustomizing, setIsPhotoCustomizing] = useState<boolean>(false);
    const [originalPhotoCustomizingData, setOriginalPhotoCustomizingData] = useState<TLogoCustomization | null>(null);
    const { updateUserInfoInStorage } = useAuthContext();
    const { techAPI2UserINfo } = useUserService();
    const configurePhotoClickHandler = () => {
        setIsPhotoCustomizing(true);
        makeDraggableCapableForImageResize(refLogoPhoto, customizePhotoMouseMoveHandler);
        setOriginalPhotoCustomizingData(userInfo.other?.logoCustomization ? { ...userInfo.other.logoCustomization } : null);
    };

    const cancelLogoCustomization = () => {
        setIsPhotoCustomizing(false);
        setLogoCustomizationStyle(originalPhotoCustomizingData || undefined);
        if (originalPhotoCustomizingData) userInfo.other.logoCustomization = originalPhotoCustomizingData;
        setOriginalPhotoCustomizingData(null);
        setDroppableCapablePhoto();
    };
    const setLogoCustomizationStyle = (p: TLogoCustomization | undefined) => {
        if (!p) {
            _setLogoCustomizationStyle({ backgroundSize: "cover" });
            return;
        }

        _setLogoCustomizationStyle({
            backgroundSize: p.percentageSize + "%",
            backgroundPositionX: p.left + "%",
            backgroundPositionY: p.top + "%",
            backgroundRepeat: "no-repeat",
        });
    };

    const updateLogo = (newfile?: string, inappropriateContent?: boolean, unsupportedImageType?: boolean, errorMessage?: string) => {
        if (errorMessage) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage?.(errorMessage);
            return;
        }
        if (inappropriateContent) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage?.("");
            setInappropriateContent(true);
            setLogo(ASSETS_ROUTES.IMGS.techAvatarUrl);
        } else if (unsupportedImageType) {
            window.scrollTo({ top: 0 });
            setUploadErrorMessage?.("");
            setUnsupportedImageType(true);
            setLogo(ASSETS_ROUTES.IMGS.techAvatarUrl);
        } else {
            if (!newfile) {
                setLogo(ASSETS_ROUTES.IMGS.techAvatarUrl);
            } else {
                const cacheKill = `?${getCacheKillQueryString()}`;
                setUploadErrorMessage?.("");
                setInappropriateContent(false);
                setUnsupportedImageType(false);
                setLogo(getImagePath(newfile, "fixed", !editable ? userInfo.guid : undefined) + cacheKill);
            }
        }
    };

    const customizePhotoMouseMoveHandler = (x: number, y: number) => {
        const values: TLogoCustomization = userInfo.other?.logoCustomization || {
            left: 0,
            percentageSize: 100,
            top: 0,
        };
        values.left -= 0.2 * x;
        values.top -= 0.2 * y;
        userInfo.other.logoCustomization = values;
        setLogoCustomizationStyle(values);
    };
    const zoomAndSaveClickHandler = (command: "zoomin" | "zoomout" | "reset") => {
        const values: TLogoCustomization = userInfo.other?.logoCustomization || {
            left: 0,
            percentageSize: 100,
            top: 0,
        };

        switch (command) {
            case "zoomin":
                values.percentageSize += 5;
                break;
            case "zoomout":
                values.percentageSize -= 5;
                break;
            case "reset":
                values.left = 0;
                values.percentageSize = 100;
                values.top = 0;
                break;
            default:
                break;
        }
        userInfo.other.logoCustomization = values;
        setLogoCustomizationStyle(values);
    };

    const setDroppableCapablePhoto = () => {
        makeDroppableCapable(refLogoPhoto, "logo", "image", "fixed", (newfile, inappropriateContent, unsupportedImageType, errorMessage) => {
            updateLogo(newfile, inappropriateContent, unsupportedImageType, errorMessage);
            props.onboardingStepCompleted?.();
        });
    };
    useEffect(() => {
        if (editable) {
            setDroppableCapablePhoto();
        }
        updateLogo(userInfo?.images?.logo);
        if (userInfo.other) {
            setLogoCustomizationStyle(userInfo.other.logoCustomization);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    const componentLogSection = (
        <>
            <div
                ref={refLogoPhoto}
                className={
                    "rounded-circle border border-2 d-inline-block position-relative " +
                    extraClass +
                    " " +
                    (isPhotoCustomizing ? "profile-logo-cursor-grabbing " : "") +
                    (workingState === "done" ? "" : "cssLoading")
                }
                style={
                    workingState === "done"
                        ? {
                              backgroundImage: `url(${!!userInfo.incognito && !editable ? ASSETS_ROUTES.IMGS.incognitoTechUrl : logoImg || ASSETS_ROUTES.IMGS.techAvatarUrl})`,

                              ...logoCustomizationStyle,
                          }
                        : logoCustomizationStyle
                }
            >
                {editable && !isSomePartBeingEdited && !isPhotoCustomizing && (
                    <FileUploaderComponent
                        relatedComponent={refLogoPhoto}
                        tooltipPosition="left"
                        fileToolTipText="Click here to edit your photo, or drag and drop it onto this area. Images should be in .JPG, .JPEG, .PNG, .HEIC or .HEIF formats. Your profile image should be minimum 500x500 pixels. "
                    />
                )}
                {editable && !isSomePartBeingEdited && !isPhotoCustomizing && (
                    <button
                        type="button"
                        className={`btn bg-white fileTooltip position-absolute left`}
                        style={{ right: "-1rem" }}
                        data-tooltip="Click here to customize your photo"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            configurePhotoClickHandler();
                        }}
                    >
                        <i className="fa-solid fa-sliders fa-md text-center shadow editIcon"></i>
                    </button>
                )}
            </div>
            {editable && (
                <div className="profile-logo-customize-buttons">
                    {editable && !isSomePartBeingEdited && isPhotoCustomizing && (
                        <>
                            <div className="text-muted fw-semibold small my-3">
                                <i className="fa-solid fa-hand-pointer me-2"></i>
                                <span>Drag to reposition your photo</span>
                            </div>
                            <div className="btn-group mb-3 gap-3 " aria-label="Photo zoom controls">
                                <button
                                    className="btn btn-light px-4 py-3 rounded-4 shadow-sm "
                                    type="button"
                                    onClick={() => zoomAndSaveClickHandler("zoomin")}
                                >
                                    <i className="fa-solid fa-magnifying-glass-plus"></i>
                                </button>
                                <button
                                    className="btn btn-light px-4 py-3 rounded-4 shadow-sm"
                                    type="button"
                                    onClick={() => zoomAndSaveClickHandler("zoomout")}
                                >
                                    <i className="fa-solid fa-magnifying-glass-minus"></i>
                                </button>
                                <button className="btn btn-light px-4 py-3 rounded-4 shadow-sm" type="button" onClick={() => zoomAndSaveClickHandler("reset")}>
                                    <i className="fa-solid fa-rotate-left"></i>
                                </button>
                            </div>
                            <br />
                            <div className="d-inline justify-content-center align-items-center gap-2 mt-3 pt-2">
                                <button
                                    className="btn btn-primary rounded-3 px-3 shadow-sm"
                                    onClick={async () => {
                                        await updateUserInfoInStorage(techAPI2UserINfo(userInfo));
                                        window.wwgTechInfo_dataWasModified = true;
                                        updateLogoCustomization(userInfo.other.logoCustomization || null);
                                        setIsPhotoCustomizing(false);
                                        setDroppableCapablePhoto();
                                    }}
                                >
                                    <i className="fa-solid fa-floppy-disk me-2"></i> Save
                                </button>
                                <button className="btn btn-link rounded-3 text-danger text-decoration-none px-2" onClick={cancelLogoCustomization}>
                                    <i className="fa-regular fa-trash-can me-2"></i> Cancel
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );

    return (
        <>
            {editable && (
                <div
                    className={
                        "my-2 p-0 text-center rounded " +
                        (!isPhotoCustomizing ? "col-lg-4" : "col-lg-12 py-3 profile-logo-position profile-logo-position-background")
                    }
                >
                    {componentLogSection}
                </div>
            )}

            {!editable && <>{componentLogSection}</>}
        </>
    );
};
